import { useQueryClient } from '@tanstack/react-query'
import { useState, useEffect, useRef, useCallback } from 'react'
import { useEmployeeDirectory } from '../contexts/EmployeeDirectoryContext'
import { useProfilePictures } from '../contexts/ProfilePictureContext'
import { apiClient } from '../common/apiClient'
import { startDemandRefresh } from '../common/demandRefresh'
import { BDM_GROUP_LABEL, isBdmTeam } from '../shared/data/division'
import {
  getCustomTeamScope,
  isHardcodedDivisionLeader,
  matchesCustomTeamScope,
} from '../shared/data/leaders'

export interface TeamMember {
  name: string
  email: string
  mobilePhone?: string
  personalMobile?: string
  profilePicture?: string
  isManager: boolean
  location?: string
  birthday?: string
  joinedDate?: string
  team?: string
  company?: string
  subdivision?: string
}

// Matrix reporting: extra reports a manager sees on top of their Manager__c
// hierarchy. Keep in sync with ADDITIONAL_REPORTS in backend/src/routes/leave.js.
const ADDITIONAL_REPORTS: Record<string, string[]> = {
  'atrinh@duotax.com.au': ['jarrel@duoinsurance.com.au'],
}

export interface UseTeamMembersOptions {
  userTeam?: string
  userCompany?: string
  loggedInUserEmail?: string
  effectiveUserEmail?: string
  filterByCompany?: boolean
  onlyDirectReports?: boolean
}

export interface UseTeamMembersReturn {
  teamMembers: TeamMember[]
  teamsByName: Record<string, TeamMember[]>
  isLoading: boolean
  showCards: boolean
  refetch: () => void
}

export const useTeamMembers = ({
  userTeam,
  userCompany,
  loggedInUserEmail,
  effectiveUserEmail,
  filterByCompany = true,
  onlyDirectReports = true,
}: UseTeamMembersOptions): UseTeamMembersReturn => {
  const queryClient = useQueryClient()
  const activeUserEmail = effectiveUserEmail || loggedInUserEmail
  const { getDirectory } = useEmployeeDirectory()
  const { getProfilePicture } = useProfilePictures()
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCards, setShowCards] = useState(false)
  const [teamsByName, setTeamsByName] = useState<Record<string, TeamMember[]>>({})
  const [refetchTrigger, setRefetchTrigger] = useState(0)

  const forceNextRead = useRef(false)
  const refetch = useCallback(() => {
    forceNextRead.current = true
    setRefetchTrigger((prev) => prev + 1)
  }, [])

  // Recheck on user activity after five minutes, with no recurring timer.
  // Ordinary reads respect the shared caches; manual refresh bypasses them.
  useEffect(() => {
    const poll = startDemandRefresh(() => setRefetchTrigger((prev) => prev + 1), 5 * 60 * 1000, {
      immediate: false,
    })
    return poll.stop
  }, [])

  useEffect(() => {
    let cancelled = false
    const fetchTeamMembers = async () => {
      if (!activeUserEmail) {
        setIsLoading(false)
        return
      }

      // Wait for user permissions to load before fetching
      // userTeam/userCompany being undefined means permissions haven't loaded yet
      if (userTeam === undefined && userCompany === undefined) {
        // Still loading permissions, don't fetch yet
        return
      }

      setIsLoading(true)
      setShowCards(false)
      const force = forceNextRead.current
      forceNextRead.current = false

      try {
        // These reads are independent; share the relationship cache across tab visits.
        if (force) await queryClient.invalidateQueries({ queryKey: ['manager-relationships'], refetchType: 'none' })
        const [coManagerData, directory] = await Promise.all([
          queryClient.fetchQuery<any[]>({
            queryKey: ['manager-relationships'],
            queryFn: () => apiClient.get<any[]>('/api/manager-relationships'),
            staleTime: 5 * 60 * 1000,
          }).catch(error => {
            console.warn('Failed to fetch co-manager relationships:', error)
            return []
          }),
          getDirectory({ force }),
        ])
        if (cancelled) return

        // Build co-manager map
        const coManagerMap = new Map<string, string[]>()
        if (Array.isArray(coManagerData)) {
          coManagerData.forEach((rel: any) => {
            if (!coManagerMap.has(rel.manager_email)) {
              coManagerMap.set(rel.manager_email, [])
            }
            if (!coManagerMap.has(rel.co_manager_email)) {
              coManagerMap.set(rel.co_manager_email, [])
            }
            coManagerMap.get(rel.manager_email)!.push(rel.co_manager_email)
            coManagerMap.get(rel.co_manager_email)!.push(rel.manager_email)
          })
        }

        // Fetch the fixed, server-owned Duo Base directory contract once. All
        // view-specific filtering below is local and cannot alter an upstream query.
        if (cancelled) return
        const currentUser = directory.find(
          (employee) => employee.Email.toLowerCase() === activeUserEmail.toLowerCase()
        )

        if (!currentUser) {
          setIsLoading(false)
          return
        }

        // Some leaders (e.g. Kristian Sese) are not flagged Division_Leader__c in
        // Salesforce. Treat them as managers anyway so everyone whose line
        // manager is them resolves through the Manager__c hierarchy below,
        // regardless of team, company or location.
        const isCurrentUserManager =
          currentUser.Division_Leader__c === true ||
          currentUser.Division_Leader__c === 'true' ||
          isHardcodedDivisionLeader(activeUserEmail)

        // Use the effective user's team/company (from Salesforce), not the logged-in user's
        const effectiveUserTeam = currentUser.Team__c
        const effectiveUserCompany = currentUser.Company__c
        const hasNoManager = !currentUser.Manager__c
        const userName = currentUser.Name
        const isTuanDuong = userName === 'Tuan Duong'

        const managerEmails: string[] = []

        if (isCurrentUserManager) {
          managerEmails.push(activeUserEmail)
          if (coManagerMap.has(activeUserEmail)) {
            const coManagers = coManagerMap.get(activeUserEmail)!
            managerEmails.push(...coManagers)
          }
        }

        let allTeamMembers: any[] = []
        const teamMap: Record<string, any[]> = {}

        const isDuoTaxCostConsultants = effectiveUserCompany === 'Duo Tax Cost Consultants'

        if (isTuanDuong || hasNoManager) {
          // Show ALL employees - only for Tuan Duong or top-level users with no manager
          allTeamMembers = directory

          allTeamMembers.forEach((member: any) => {
            const teamName =
              (isBdmTeam(member.Team__c) ? BDM_GROUP_LABEL : member.Team__c) || 'Other'
            if (!teamMap[teamName]) {
              teamMap[teamName] = []
            }
            teamMap[teamName].push(member)
          })
        } else if (isDuoTaxCostConsultants) {
          // Duo Tax Cost Consultants - show all employees in their company (one big team, no hierarchy)
          allTeamMembers = directory.filter(
            (employee) => employee.Company__c === 'Duo Tax Cost Consultants'
          )
          teamMap['Duo Tax Cost Consultants'] = allTeamMembers
        } else if (isCurrentUserManager && managerEmails.length > 0) {
          // Manager/Division Leader - fetch all descendants + same team members
          const allEmployees = directory

          if (!allEmployees || allEmployees.length === 0) {
            setIsLoading(false)
            return
          }

          // Build hierarchy map
          const managerToReportsMap = new Map<string, any[]>()
          allEmployees.forEach((emp: any) => {
            if (emp.Manager__c) {
              if (!managerToReportsMap.has(emp.Manager__c)) {
                managerToReportsMap.set(emp.Manager__c, [])
              }
              managerToReportsMap.get(emp.Manager__c)!.push(emp)
            }
          })

          // Recursive function to get all descendants
          const getAllDescendants = (managerId: string, visited = new Set<string>()): any[] => {
            if (visited.has(managerId)) return []
            visited.add(managerId)

            const directReports = managerToReportsMap.get(managerId) || []
            let allDescendants = [...directReports]

            directReports.forEach((report: any) => {
              const descendants = getAllDescendants(report.Id, visited)
              allDescendants.push(...descendants)
            })

            return allDescendants
          }

          // Get manager IDs. Emails are compared case-insensitively so a casing
          // mismatch between the signed-in address and the Salesforce Email
          // never silently drops a leader's whole reporting line.
          const managerEmailSet = new Set(managerEmails.map((email) => email.toLowerCase()))
          const isManagerEmail = (email?: string) =>
            !!email && managerEmailSet.has(email.toLowerCase())

          const managerIds: string[] = []
          for (const mgrEmail of managerEmails) {
            const manager = allEmployees.find(
              (emp: any) => emp.Email?.toLowerCase() === mgrEmail.toLowerCase()
            )
            if (manager) {
              managerIds.push(manager.Id)
            }
          }

          // Get all descendants
          const allDescendantsSet = new Set<string>()
          const descendantsArray: any[] = []

          managerIds.forEach((managerId) => {
            const descendants = getAllDescendants(managerId)
            descendants.forEach((desc: any) => {
              if (!allDescendantsSet.has(desc.Id)) {
                allDescendantsSet.add(desc.Id)
                descendantsArray.push(desc)
              }
            })
          })

          // Inject any matrix-reporting extra reports for this manager
          const extraReportEmails = ADDITIONAL_REPORTS[activeUserEmail?.toLowerCase() || ''] || []
          extraReportEmails.forEach((reportEmail) => {
            const contact = allEmployees.find(
              (emp: any) => emp.Email?.toLowerCase() === reportEmail
            )
            if (contact && !allDescendantsSet.has(contact.Id)) {
              allDescendantsSet.add(contact.Id)
              descendantsArray.push(contact)
            }
          })

          // Custom team scoping: leaders who oversee a division + location pool
          // that sits outside their Manager__c line. Kristian Sese oversees the
          // Philippines BDM group ("Business Development Manager" + "Sales"),
          // several of whom report to other managers in Salesforce. Mirrors the
          // backend leave scoping in routes/leave.js.
          const customTeamScope = getCustomTeamScope(activeUserEmail)
          if (customTeamScope) {
            allEmployees.forEach((emp: any) => {
              if (allDescendantsSet.has(emp.Id) || isManagerEmail(emp.Email)) return
              if (!matchesCustomTeamScope(customTeamScope, emp.Team__c, emp.Division_Location__c)) {
                return
              }
              allDescendantsSet.add(emp.Id)
              descendantsArray.push(emp)
            })
          }

          // Include managers
          const managerContacts = allEmployees.filter((emp: any) => isManagerEmail(emp.Email))

          // Optionally include team members with same Team__c (when onlyDirectReports is false)
          const isSameTeam = (team: string | undefined) =>
            isBdmTeam(effectiveUserTeam) ? isBdmTeam(team) : team === effectiveUserTeam

          const sameTeamMembers =
            !onlyDirectReports && effectiveUserTeam
              ? allEmployees.filter(
                  (emp: any) =>
                    isSameTeam(emp.Team__c) &&
                    (!filterByCompany || emp.Company__c === effectiveUserCompany) &&
                    !allDescendantsSet.has(emp.Id) &&
                    !isManagerEmail(emp.Email)
                )
              : []

          const allTeamData = [...managerContacts, ...descendantsArray, ...sameTeamMembers]

          // Deduplicate by Id
          const seenIds = new Set<string>()
          const deduplicatedTeamData = allTeamData.filter((member: any) => {
            if (seenIds.has(member.Id)) return false
            seenIds.add(member.Id)
            return true
          })

          deduplicatedTeamData.forEach((member: any) => {
            const teamName =
              (isBdmTeam(member.Team__c) ? BDM_GROUP_LABEL : member.Team__c) || 'Team'
            if (!teamMap[teamName]) {
              teamMap[teamName] = []
            }
            teamMap[teamName].push(member)
          })

          allTeamMembers = deduplicatedTeamData
        } else if (effectiveUserTeam && effectiveUserCompany) {
          // Non-manager - show team based on Team__c (and optionally Company__c)
          const matchesTeam = (team: string | undefined) =>
            isBdmTeam(effectiveUserTeam) ? isBdmTeam(team) : team === effectiveUserTeam

          allTeamMembers = directory.filter(
            (employee) =>
              matchesTeam(employee.Team__c) &&
              (!filterByCompany || employee.Company__c === effectiveUserCompany)
          )
          const groupLabel = isBdmTeam(effectiveUserTeam) ? BDM_GROUP_LABEL : effectiveUserTeam
          teamMap[groupLabel || 'Team'] = allTeamMembers
        } else {
          // No valid condition met - don't show any team members
          // This prevents showing all employees when permissions aren't properly loaded
          console.warn('useTeamMembers: No valid team condition met, returning empty')
          setIsLoading(false)
          return
        }

        const salesforceData = allTeamMembers

        if (!salesforceData || salesforceData.length === 0) {
          setIsLoading(false)
          return
        }

        // Map contacts to team members using Duo Cloud profile pictures
        const membersWithPhotos = salesforceData.map((contact: any) => {
          const email = contact.Email.toLowerCase()
          const isTuan = email === 'tuan@duotax.com.au'
          const isAndy = email === 'andy@duotax.com.au'

          return {
            name: contact.Name,
            email: contact.Email,
            mobilePhone: contact.MobilePhone,
            personalMobile: contact.Internal_Mobile__c,
            profilePicture: getProfilePicture(contact.Email),
            isManager:
              isTuan ||
              isAndy ||
              contact.Division_Leader__c === true ||
              contact.Division_Leader__c === 'true',
            location: contact.Division_Location__c,
            birthday: contact.Birthday__c,
            joinedDate: contact.Joined_Date__c,
            team: contact.Team__c,
            company: contact.Company__c,
            subdivision: contact.Subdivision__c,
          }
        })

        setTeamMembers(membersWithPhotos)

        // Group by subdivision only for BDM team managers (excluding Andy)
        const groupedByTeam: Record<string, TeamMember[]> = {}
        const isBdmManager =
          isCurrentUserManager &&
          isBdmTeam(effectiveUserTeam) &&
          activeUserEmail?.toLowerCase() !== 'andy@duotax.com.au'
        const hasSubdivisions = isBdmManager && membersWithPhotos.some((m) => m.subdivision)

        // "Sales" and "Business Development Manager" are one group to the user.
        const normaliseGroupKey = (team: string | undefined): string | undefined =>
          isBdmTeam(team) ? BDM_GROUP_LABEL : team

        const getSubdivisionDisplayName = (code: string | undefined): string => {
          const subdivisionNames: Record<string, string> = {
            BA: "Buyer's Agent",
            PM: 'Property Manager',
          }
          return code ? subdivisionNames[code] || code : 'No Subdivision'
        }

        membersWithPhotos.forEach((member) => {
          const groupKey = hasSubdivisions
            ? getSubdivisionDisplayName(member.subdivision)
            : normaliseGroupKey(member.team) || effectiveUserCompany || 'Team'
          if (!groupedByTeam[groupKey]) {
            groupedByTeam[groupKey] = []
          }
          groupedByTeam[groupKey].push(member)
        })

        // Sort teams - Tuan first, Andy second
        Object.keys(groupedByTeam).forEach((teamName) => {
          groupedByTeam[teamName].sort((a, b) => {
            const aTuan = a.email.toLowerCase() === 'tuan@duotax.com.au'
            const bTuan = b.email.toLowerCase() === 'tuan@duotax.com.au'
            const aAndy = a.email.toLowerCase() === 'andy@duotax.com.au'
            const bAndy = b.email.toLowerCase() === 'andy@duotax.com.au'

            if (aTuan) return -1
            if (bTuan) return 1
            if (aAndy) return -1
            if (bAndy) return 1

            return 0
          })
        })

        setTeamsByName(groupedByTeam)
        setIsLoading(false)
      } catch (error) {
        if (cancelled) return
        console.error('Error fetching team members:', error)
        setIsLoading(false)
      }
    }

    fetchTeamMembers()
    return () => {
      cancelled = true
    }
  }, [
    userTeam,
    userCompany,
    activeUserEmail,
    getDirectory,
    getProfilePicture,
    refetchTrigger,
    filterByCompany,
    onlyDirectReports,
  ])

  // Trigger animation. Cleared on unmount, so it never sets state after the component (or a test) is gone.
  useEffect(() => {
    if (isLoading || teamMembers.length === 0) return
    const timer = setTimeout(() => setShowCards(true), 50)
    return () => clearTimeout(timer)
  }, [isLoading, teamMembers.length])

  return {
    teamMembers,
    teamsByName,
    isLoading,
    showCards,
    refetch,
  }
}
